import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["currentIndex", "maxDotsToDisplay", "total"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { PhotoCarouselDot } from '../PhotoCarouselDot/PhotoCarouselDot';
import { StyledPhotoCarouselDots } from './PhotoCarouselDots.styles';

/**
 * Container for multiple `PhotoCarousel.Dot` components.
 *
 * @since 8.66.0
 */
export var PhotoCarouselDots = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var currentIndex = _ref.currentIndex,
    maxDotsToDisplay = _ref.maxDotsToDisplay,
    total = _ref.total,
    rest = _objectWithoutProperties(_ref, _excluded);
  if (total < 2) {
    return [];
  }
  var paginationDots = [];

  /**
   * Normalize `currentIndex`, converting negative numbers or numbers greater than total.
   * Use result as `activeIndex`.
   */
  var indexOffset = Math.abs(currentIndex) % total;
  var activeIndex = currentIndex < 0 ? (total - indexOffset) % total : indexOffset;

  /**
   * Since there might be a lot of slides, we don't show a pagination dot for every
   * slide in the carousel.
   *
   * If there are more slides beyond the last dot, make that dot smaller
   * to show there is "continuation". Give similar treatment to the first dot.
   */
  var totalDots = Math.min(total, maxDotsToDisplay);
  var middle = Math.floor(maxDotsToDisplay / 2);
  var overflowing = total > maxDotsToDisplay;

  // Determine if there are more slides beyond the left and/or right of the dots.
  var overflowingLeft = overflowing && activeIndex > middle;
  var overflowingRight = overflowing && activeIndex + middle < total - 1;
  for (var i = 0; i < totalDots; i += 1) {
    // Determine if this is a "continuation" dot. If it is, it will display
    // smaller than the others as a visual affordance.
    var isFirstDot = i === 0;
    var isLastDot = i === totalDots - 1;
    var continuationLeft = isFirstDot && overflowingLeft;
    var continuationRight = isLastDot && overflowingRight;
    var isContinuation = continuationLeft || continuationRight;

    // Calculate this dot's slide index and whether it's the active slide
    var active = false;
    var slideIndex = 0;
    if (overflowingLeft) {
      if (overflowingRight) {
        active = i === middle;
        slideIndex = activeIndex - middle + i;
      } else {
        active = i === maxDotsToDisplay - (total - activeIndex);
        slideIndex = total - maxDotsToDisplay + i;
      }
    } else {
      active = i === activeIndex;
      slideIndex = i;
    }
    paginationDots.push( /*#__PURE__*/React.createElement(PhotoCarouselDot, {
      isActive: active,
      isContinuation: isContinuation,
      key: slideIndex
    }));
  }
  return /*#__PURE__*/React.createElement(StyledPhotoCarouselDots, _extends({
    ref: forwardedRef
  }, rest), paginationDots);
});
PhotoCarouselDots.displayName = 'PhotoCarouselDots';
PhotoCarouselDots.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The index of the active slide.
   */
  currentIndex: PropTypes.number.isRequired,
  /**
   * The maximum number of dots to display.
   */
  maxDotsToDisplay: PropTypes.number,
  /**
   * The total number of slides in the carousel.
   */
  total: PropTypes.number.isRequired
} : {};
PhotoCarouselDots.defaultProps = {
  maxDotsToDisplay: 5
};